/**
 * Citi brand style configuration — colors, typography, and prompt rules
 * derived from the PitchPres Brand Style Guide v2 (June 2024).
 *
 * Toggled on/off via a "Citi style" checkbox in both the chat and deck
 * creation UIs.  When active the prompt text below is appended to the
 * system prompt, and the color constants below are used as renderer defaults.
 */

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#')
// ---------------------------------------------------------------------------

export const CITI_COLORS = {
  citiBlue: "255BE3",
  citiRed: "FF3C28",
  citiInk: "0F1632",
  white: "FFFFFF",

  gray01: "A4ACAF",
  gray02: "BCC5C9",
  gray03: "D3DADD",
  gray04: "E6EBED",

  sand01: "BB9A71",
  sand02: "D5C1A8",
  sand03: "DFD0BE",
  sand04: "EAE0D3",

  blueLight: "73C2FC",
  blueGreenLight: "80CE84",
  forestBright: "388A42",
  tanBright: "FAB728",
  yellow: "FFCD00",
  orangeLight: "FFA15A",
} as const;

/**
 * Drop-in ThemeColors override when Citi style is active.
 * Values from the authoritative "PitchPres A4 Landscape" slide master
 * colorScheme and fontScheme.
 */
export const CITI_THEME_COLORS = {
  dark1: CITI_COLORS.citiInk,        // #0F1632 — Text1
  dark2: CITI_COLORS.citiRed,         // #FF3C28 — Text2
  light1: CITI_COLORS.white,          // #FFFFFF — Background1
  light2: CITI_COLORS.gray03,         // #D3DADD — Background2
  accent1: CITI_COLORS.citiBlue,      // #255BE3
  accent2: CITI_COLORS.citiInk,       // #0F1632
  accent3: CITI_COLORS.blueLight,     // #73C2FC
  accent4: CITI_COLORS.gray01,        // #A4ACAF
  accent5: CITI_COLORS.forestBright,  // #388A42
  accent6: CITI_COLORS.blueGreenLight, // #80CE84
  hyperlink: "245BE2",
  followedHyperlink: "245BE2",
  majorFont: "Citi Sans Display",
  minorFont: "Citi Sans Text",
} as const;

/** Ordered series palette — Citi blue first, then contrasting accents. */
export const CITI_CHART_SERIES_PALETTE = [
  CITI_COLORS.citiBlue,
  CITI_COLORS.blueLight,
  CITI_COLORS.blueGreenLight,
  CITI_COLORS.forestBright,
  CITI_COLORS.tanBright,
  CITI_COLORS.sand01,
  CITI_COLORS.yellow,
  CITI_COLORS.orangeLight,
];

// ---------------------------------------------------------------------------
// Slide dimensions & layout positions (from PitchPres A4 Landscape master)
// ---------------------------------------------------------------------------

export const CITI_SLIDE_DIMENSIONS = { width: 780, height: 540 } as const;

export const CITI_POSITIONS = {
  title: { left: 21.5, top: 17.7, width: 737, height: 56.7 },
  pageMessage: { left: 21.5, top: 80, width: 737, height: 40.5 },
  footer: { left: 21.5, top: 515.6, width: 641.4, height: 14.2 },
  contentStart: { left: 12.8, top: 131.1 },
  accentBar: { left: 21.6, top: 7.2, width: 56.9, height: 10.1 },
} as const;

export const CITI_LAYOUT_NAMES = [
  "Title Slide",
  "Title Slide Divider 01",
  "TOC 01",
  "Title & Content",
  "Title & Content with Message",
  "Title Only",
  "Title & Message only",
  "Two Column Content",
  "Two Column Content with Message",
  "Pro Cons Two Column",
  "Pro Cons Two Column with Message",
  "Three Column Content",
  "Three Column Content with Message",
  "Quad Content",
  "Quad Content with Message",
  "Two Row Content",
  "Two Row Content with Message",
  "Narrow & Wide Content",
  "Narrow & Wide Content with Message",
  "Two Column Three Content",
  "Two Column Three Content with Message",
  "Left Box Content",
  "Left Box Content with Message",
  "Right Box Content",
  "Right Box Content with Message",
  "Content & Left Image",
  "Content & Left Image with Message",
  "Content & Right Image",
  "Content & Right Image with Message",
  "Disclaimer",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when Citi style is active
// ---------------------------------------------------------------------------

export const CITI_STYLE_PROMPT = `

## CITI BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. The Citi font families are:
- Titles: \`"Citi Sans Display"\`
- All other text (body, bullets, tables, charts, headings): \`"Citi Sans Text"\`
- Footnotes & small annotations only: \`"Citi Sans Condensed"\`
NEVER omit fontFamily when Citi style is active. If a shape already has the wrong font, include fontFamily to correct it.

### Color palette (from PitchPres master colorScheme)
Use ONLY these Citi brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Citi ink #${CITI_COLORS.citiInk}
- Text2 / dark2: Citi red #${CITI_COLORS.citiRed}
- Background1 / light1: White #${CITI_COLORS.white}
- Background2 / light2: Gray 03 #${CITI_COLORS.gray03}
- Accent1: Citi blue #${CITI_COLORS.citiBlue}
- Accent2: Citi ink #${CITI_COLORS.citiInk}
- Accent3: Blue light #${CITI_COLORS.blueLight}
- Accent4: Gray 01 #${CITI_COLORS.gray01}
- Accent5: Forest bright #${CITI_COLORS.forestBright}
- Accent6: Blue-green light #${CITI_COLORS.blueGreenLight}

**Usage:**
- **Primary brand**: Citi blue #${CITI_COLORS.citiBlue}
- **Text / ink**: Citi ink #${CITI_COLORS.citiInk}
- **Lines & borders**: Gray 01 #${CITI_COLORS.gray01}
- **Background / shading**: Gray 04 #${CITI_COLORS.gray04} or Sand 04 #${CITI_COLORS.sand04}
- **Highlight rows**: Sand 04 #${CITI_COLORS.sand04}
- **Accent (sparingly)**: Citi red #${CITI_COLORS.citiRed} (trend lines or map markers only)
- **Chart series palette** (in order): ${CITI_CHART_SERIES_PALETTE.join(", ")}
  Always use Citi blue (#${CITI_COLORS.citiBlue}) as the first/primary series color.

### Typography (authoritative — from PitchPres A4 Landscape master)
Slide dimensions: 780 × 540 pt.

**Placeholder positions:**
- **Title**: left=21.5, top=17.7, width=737, height=56.7 — "Citi Sans Display" 22pt, Citi blue (${CITI_COLORS.citiBlue}), left-aligned, verticalAlignment="bottom"
- **Page message / subtitle**: left=21.5, top=80, width=737, height=40.5 — "Citi Sans Text" 14pt bold, Citi ink (${CITI_COLORS.citiInk})
- **Content area**: starts at left=12.8, top=131.1
- **Footer**: left=21.5, top=515.6, width=641.4, height=14.2 — "Citi Sans Condensed" 8pt, Citi ink, verticalAlignment="bottom"

**Content text hierarchy (indent levels from the master):**
| Level | Role | Font | Size | Weight | Color | Bullet | Bullet color |
|-------|------|------|------|--------|-------|--------|-------------|
| 1 | Section heading | Citi Sans Text | 12pt | bold | Citi blue | none | — |
| 2 | Subheading | Citi Sans Text | 11pt | bold | Citi ink | none | — |
| 3 | Bullet (•) | Citi Sans Text | 10pt | regular | Citi ink | • (U+2022) | Citi blue |
| 4 | Sub-bullet (○) | Citi Sans Text | 10pt | regular | Citi ink | ○ (U+25CB) 70% size | Citi blue |
| 5 | Sub-sub-bullet (–) | Citi Sans Text | 10pt | regular | Citi ink | – (U+2013) | Citi blue |

- **Footnotes / small annotations**: "Citi Sans Condensed", 8pt, Citi ink
- **Highlighting**: bold Citi ink preferred; Citi blue for extra emphasis; green/red only for growth/loss figures
- All text is sentence case, single-spaced, left-justified unless readability requires otherwise.
- Space before: L1=12pt, L2=6pt, L3=6pt, L4=3pt, L5=3pt.

### Charts (Citi style)
- Font: "Citi Sans Text" (or "Citi Sans Condensed" when tight), minimum 8pt
- **Primary data color**: always Citi blue (${CITI_COLORS.citiBlue}) unless it hurts legibility
- Multi-series: use the series palette above in order
- Data labels: bold, positioned outside bars/columns (inside for stacked)
- Gridlines: NONE by default. Only acceptable when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- Category axis: 0.5pt Gray 01 (${CITI_COLORS.gray01}) line
- Value axis: NO axis lines (except Bubble/Scatter charts)
- No 3D effects, gradients, or graphic fills
- Non-Citi highlight: Citi red 1.0pt outline
- CAGR lines: Tan bright (${CITI_COLORS.tanBright}), 1.0pt

### Tables (Citi style)
- **Data tables**: 0.75pt Citi blue (${CITI_COLORS.citiBlue}) border above header row and below last data row. Table title: 12pt bold Citi blue. Table units: 8pt not bold Citi ink. Column headings: Citi blue, same point size as figures, left-aligned above columns.
- **Text tables**: header row Citi blue fill with white text; left column Citi ink fill with white text and 0.5pt white separator. Bottom border 0.75pt Citi ink.
- Alternate row shading: Gray 04 (${CITI_COLORS.gray04}) for readability
- Highlight rows/columns: Sand 04 (${CITI_COLORS.sand04}), bold Citi blue text
- Citi Blue fill can be used to highlight Totals
- Font: "Citi Sans Text" for table body (10pt), "Citi Sans Condensed" 8pt for footnotes

### Shapes & layout (Citi style)
- Use modular shapes (rounded corners) to separate content — NOT divider lines
- Modular shape options: (a) 0.5pt Gray 01 outline, no fill; (b) Gray 04 fill, no outline; (c) Sand 04 fill, no outline
- Tombstones / org chart boxes: rounded corners when standalone, sharp when inside a modular shape
- Connectors: solid 0.5pt Gray 01 lines
- Page numbers: 8pt, Gray 01, "Citi Sans Text"

### Citi-specific snippets (USE THESE instead of generic snippets when Citi style is active)

**citiTextTable** — Citi-branded text table with blue header fill + ink left column.
- First item = column headers (label = left header, description = pipe-separated remaining headers). Remaining items = data rows. Set \`bold: true\` for highlight rows.
- Example:
\`\`\`json
{ "type": "citiTextTable", "title": "Peer comparison", "items": [
  { "label": "Metric", "description": "Company A|Company B|Company C" },
  { "label": "Revenue", "description": "$1.2B|$890M|$2.1B" },
  { "label": "EBITDA margin", "description": "32%|28%|35%", "bold": true }
]}
\`\`\`

**citiQuadrant** — 2×2 content grid matching the Citi template quadrant layout.
- Each item = one quadrant cell (top-left, top-right, bottom-left, bottom-right). Set \`color\` to give a cell a Sand 04 fill (description panel). Use \`bullets\` for bullet content.
- Example:
\`\`\`json
{ "type": "citiQuadrant", "title": "Market positioning overview", "items": [
  { "label": "Revenue breakdown", "color": "#EAE0D3", "bullets": ["Americas: 45%", "EMEA: 30%", "APAC: 25%"] },
  { "label": "Competitive landscape", "bullets": ["#2 market share globally", "Gaining vs top competitor"] },
  { "label": "Growth drivers", "bullets": ["Digital transformation", "Cross-sell into base"] },
  { "label": "Key metrics", "bullets": ["Revenue: $12.4B", "EBITDA margin: 32%"] }
]}
\`\`\`

**citiSplitContent** — Two-column split: left description panel + right structured content.
- Items[0] = left panel (label = heading, bullets = body). Set \`color\` for Sand 04 fill. Items[1] = right heading (label = title, description = subtitle). Items[2+] = right-side data rows (label + pipe-separated description values). Set \`bold: true\` for highlight/total rows.
- Example:
\`\`\`json
{ "type": "citiSplitContent", "title": "Valuation methodology", "items": [
  { "label": "Approach", "color": "#EAE0D3", "bullets": ["DCF with 10-year forecast", "WACC of 8.5%", "Terminal growth 2.5%"] },
  { "label": "Sum-of-the-parts", "description": "US$ millions" },
  { "label": "Core banking", "description": "4,200|5,100" },
  { "label": "Wealth management", "description": "2,800|3,400" },
  { "label": "Enterprise value", "description": "8,150|10,050", "bold": true }
]}
\`\`\`

**citiTombstone** — Grid of rounded-rectangle deal/credential tombstone cards (6 per row, up to 18).
- Each item = one deal card. \`label\` = deal description (use \\n for line breaks). \`description\` = pipe-separated "deal size|date". \`color\` controls card style: "#255BE3" = Citi Blue highlight (white text), "#E6EBED" = gray, "#EAE0D3" = sand, unset = white with gray outline. Set \`bold: true\` for emphasized deal size.
- Example:
\`\`\`json
{ "type": "citiTombstone", "title": "Selected M&A credentials", "items": [
  { "label": "Acme Corp\\nAcquisition of Beta Inc.", "description": "$2.4B|Jan 2024", "color": "#255BE3", "bold": true },
  { "label": "Delta Holdings\\nSale to Gamma", "description": "$1.8B|Mar 2024", "color": "#255BE3", "bold": true },
  { "label": "Omega Systems\\nIPO", "description": "$950M|Jun 2024", "color": "#E6EBED" },
  { "label": "Sigma Partners\\nLBO", "description": "$3.1B|Sep 2023" },
  { "label": "Kappa Industries\\nMerger", "description": "$4.5B|Nov 2023" },
  { "label": "Zeta Corp\\nMinority sale", "description": "$620M|Dec 2023" }
]}
\`\`\`

**citiMatrix** — Matrix table with blue header row + blue left column, rich cell content.
- Items[0] = column headers (label = top-left corner text or empty, description = pipe-separated column headings). Items[1+] = data rows (label = row heading, description = pipe-separated cell content). Use \\n within cells for bullet lines. Set \`bold: true\` on a row for Sand 04 highlight.
- Example:
\`\`\`json
{ "type": "citiMatrix", "title": "Competitive positioning", "items": [
  { "label": "", "description": "Market share|Revenue growth|Margins" },
  { "label": "Company A", "description": "Leading (#1)\\n• 35% share|Strong\\n• 18% YoY|Expanding\\n• 32% EBITDA" },
  { "label": "Company B", "description": "Challenger (#2)\\n• 22% share|Moderate\\n• 12% YoY|Stable\\n• 28% EBITDA", "bold": true },
  { "label": "Company C", "description": "Niche (#5)\\n• 8% share|Slow\\n• 5% YoY|Compressing\\n• 18% EBITDA" }
]}
\`\`\`

**When Citi style is active, PREFER these Citi snippets over generic ones:**
- Financial tables / comparisons → \`citiTextTable\` or \`citiMatrix\`
- 2×2 overview / comparison layouts → \`citiQuadrant\`
- Description + data side-by-side → \`citiSplitContent\`
- Deal credentials / tombstone pages → \`citiTombstone\`
- Matrix comparisons (row vs column) → \`citiMatrix\`
- You may still use generic snippets (statGrid, processFlow, etc.) when the Citi snippets don't fit the content structure.

**TEXT FITTING RULE**: For processFlow and similar snippets, keep item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions will overflow. Use concise labels, not full sentences.
`;
